import * as THREE from 'three';

/**
 * Off-screen indicator for the selected navigation target.
 *
 * `WorldMarkerLayer` draws a dot at the target's actual position, which is
 * only useful while it's inside the frustum. This is the other half: a
 * triangle pinned near the screen edge and rotated to point toward wherever
 * the target actually is, for when it isn't. Sized in world units at a fixed
 * distance from the camera rather than with `sizeAttenuation: false`, since
 * that distance never changes frame to frame — simpler than fighting the
 * sprite scale shader for a constant on-screen size.
 */
export class TargetArrow {
  readonly sprite: THREE.Sprite;
  private readonly material: THREE.SpriteMaterial;
  private readonly texture: THREE.CanvasTexture;

  constructor(color = 0xffd166, scale = 3.2) {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(32, 2);
    ctx.lineTo(58, 60);
    ctx.lineTo(32, 46);
    ctx.lineTo(6, 60);
    ctx.closePath();
    ctx.fill();

    this.texture = new THREE.CanvasTexture(canvas);
    this.material = new THREE.SpriteMaterial({
      map: this.texture,
      color,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    this.sprite = new THREE.Sprite(this.material);
    this.sprite.scale.set(scale, scale, 1);
    this.sprite.renderOrder = 999;
    this.sprite.visible = false;
    this.sprite.frustumCulled = false;
  }

  /** `rollRadians` rotates the arrow's rest pose (pointing up) to point outward. */
  show(position: THREE.Vector3, rollRadians: number): void {
    this.sprite.visible = true;
    this.sprite.position.copy(position);
    this.material.rotation = rollRadians;
  }

  hide(): void {
    this.sprite.visible = false;
  }

  dispose(): void {
    this.texture.dispose();
    this.material.dispose();
  }
}
